import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, Mail, CheckCircle, AlertCircle, KeyRound } from 'lucide-react';

export function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Por favor, informe seu e-mail');
      return;
    }

    if (!email.includes('@')) {
      setError('Formato de e-mail inválido');
      return;
    }

    setIsLoading(true);

    // Simular envio de e-mail
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: 'var(--background)', fontFamily: 'var(--font-family-inter)' }}
      >
        <div
          className="w-full max-w-md p-8"
          style={{
            backgroundColor: 'var(--card)',
            borderRadius: 'var(--radius-card)',
            border: '1px solid',
            borderColor: 'var(--border)',
          }}
        >
          {/* Header idêntico ao Login */}
          <div className="text-center mb-8">
            <h1
              style={{
                color: 'var(--primary)',
                marginBottom: '8px',
                fontFamily: 'var(--font-family-inter)',
              }}
            >
              SimulaDesk
            </h1>
            <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
              Treinamento e auditoria de atendimento com IA
            </p>
          </div>

          {/* Ícone de sucesso */}
          <div className="flex justify-center mb-6">
            <div
              className="flex items-center justify-center"
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'color-mix(in srgb, var(--success) 15%, transparent)',
                color: 'var(--success)',
              }}
            >
              <CheckCircle size={32} />
            </div>
          </div>

          <div className="text-center" style={{ marginBottom: '24px' }}>
            <h2
              style={{
                fontWeight: 'var(--font-weight-semibold)',
                fontSize: 'var(--text-lg)',
                marginBottom: '12px',
                fontFamily: 'var(--font-family-inter)',
              }}
            >
              E-mail Enviado!
            </h2>
            <p
              style={{
                color: 'var(--muted-foreground)',
                fontSize: 'var(--text-sm)',
                lineHeight: '1.6',
                marginBottom: '16px',
              }}
            >
              Enviamos instruções para redefinição de senha para{' '}
              <strong style={{ color: 'var(--foreground)' }}>{email}</strong>
            </p>
            <div
              className="p-3"
              style={{
                backgroundColor: 'var(--muted)',
                borderRadius: 'var(--radius)',
                fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
                lineHeight: '1.5',
              }}
            >
              Verifique sua caixa de entrada e spam. O link de redefinição expira em 24 horas.
            </div>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-full px-6 py-3 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              borderRadius: 'var(--radius-button)',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-inter)',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--text-sm)',
            }}
          >
            Voltar para Login
          </button>

          <button
            onClick={() => {
              setIsSubmitted(false);
              setEmail('');
            }}
            className="w-full px-6 py-2.5 mt-3 transition-opacity hover:opacity-70"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--muted-foreground)',
              borderRadius: 'var(--radius-button)',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-inter)',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--text-sm)',
            }}
          >
            Tentar outro e-mail
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: 'var(--background)', fontFamily: 'var(--font-family-inter)' }}
    >
      <div
        className="w-full max-w-md p-8"
        style={{
          backgroundColor: 'var(--card)',
          borderRadius: 'var(--radius-card)',
          border: '1px solid',
          borderColor: 'var(--border)',
        }}
      >
        {/* Header idêntico ao Login */}
        <div className="text-center mb-8">
          <h1
            style={{
              color: 'var(--primary)',
              marginBottom: '8px',
              fontFamily: 'var(--font-family-inter)',
            }}
          >
            SimulaDesk
          </h1>
          <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
            Treinamento e auditoria de atendimento com IA
          </p>
        </div>

        {/* Ícone de cadeado + título da página */}
        <div className="flex justify-center mb-4">
          <div
            className="flex items-center justify-center"
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: 'color-mix(in srgb, var(--primary) 12%, transparent)',
              color: 'var(--primary)',
            }}
          >
            <KeyRound size={28} />
          </div>
        </div>

        <div className="text-center" style={{ marginBottom: '24px' }}>
          <h2
            style={{
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: 'var(--text-lg)',
              marginBottom: '8px',
              fontFamily: 'var(--font-family-inter)',
            }}
          >
            Recuperar Senha
          </h2>
          <p
            style={{
              color: 'var(--muted-foreground)',
              fontSize: 'var(--text-sm)',
              lineHeight: '1.5',
            }}
          >
            Digite seu e-mail cadastrado e enviaremos instruções para redefinir sua senha.
          </p>
        </div>

        {/* Alerta de erro — mesmo estilo do Login */}
        {error && (
          <div
            className="flex items-center gap-3 p-4 mb-6"
            style={{
              backgroundColor: 'var(--error-muted)',
              border: '1px solid color-mix(in srgb, var(--error) 30%, transparent)',
              color: 'var(--error)',
              borderRadius: 'var(--radius)',
            }}
          >
            <AlertCircle size={20} style={{ flexShrink: 0 }} />
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
              {error}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block mb-2"
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                fontFamily: 'var(--font-family-inter)',
              }}
            >
              E-mail
            </label>
            <div className="relative">
              <Mail
                size={18}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted-foreground)',
                }}
              />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="seu@email.com"
                className="w-full pl-10 pr-4 py-3 border"
                style={{
                  backgroundColor: 'var(--input-background)',
                  borderColor: error ? 'var(--error)' : 'var(--border)',
                  borderRadius: 'var(--radius-input)',
                  color: 'var(--foreground)',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-6 py-3 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              borderRadius: 'var(--radius-button)',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              fontFamily: 'var(--font-family-inter)',
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {isLoading ? 'Enviando...' : 'Enviar Instruções'}
          </button>
        </form>

        {/* Voltar ao Login — mesmo estilo de link do Login */}
        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 transition-opacity hover:opacity-70"
            style={{
              color: 'var(--primary)',
              textDecoration: 'none',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family-inter)',
            }}
          >
            <ArrowLeft size={16} />
            Voltar para Login
          </Link>
        </div>
      </div>
    </div>
  );
}
